import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject, of, forkJoin } from 'rxjs';
import { catchError, map, retry, delay, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { StorageService } from './storage.service';
import {
    TranslationErrorHandlerService,
    TranslationError,
} from './translation-error-handler.service';
import { LazyTranslationLoaderService } from './lazy-translation-loader.service';
import { TranslationPerformanceService } from './translation-performance.service';
import { TranslationBuildOptimizerService } from './translation-build-optimizer.service';
import {
    DEFAULT_LANGUAGE_CODE,
    FALLBACK_LANGUAGE_CODE,
    SUPPORTED_LANGUAGES,
    isSupportedLanguage,
    getLanguageConfig,
    getDefaultLanguageConfig,
} from '../config/translation.config';
import { LanguageConfig } from '../models/Translation';

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private currentLanguageSubject = new BehaviorSubject<string>(
        DEFAULT_LANGUAGE_CODE
    );
    public currentLanguage$ = this.currentLanguageSubject.asObservable();

    private translationLoadingSubject = new BehaviorSubject<boolean>(false);
    public translationLoading$ = this.translationLoadingSubject.asObservable();

    private translationErrorSubject = new BehaviorSubject<string | null>(null);
    public translationError$ = this.translationErrorSubject.asObservable();

    constructor(
        private translateService: TranslateService,
        private storageService: StorageService,
        private errorHandler: TranslationErrorHandlerService,
        private lazyLoader: LazyTranslationLoaderService,
        private performanceService: TranslationPerformanceService,
        private buildOptimizer: TranslationBuildOptimizerService
    ) {
        this.initializeTranslations();
    }

    /**
     * Initialize the translation service
     */
    private initializeTranslations(): void {
        // Set up error handling
        this.translateService.onTranslationChange.subscribe(() => {
            this.translationErrorSubject.next(null);
        });

        this.translateService.onLangChange.subscribe((event) => {
            this.currentLanguageSubject.next(event.lang);
            this.translationErrorSubject.next(null);
        });

        // Determine initial language with priority order:
        // 1. Saved language preference
        // 2. Browser detected language
        // 3. Default language
        const initialLanguage = this.determineInitialLanguage();

        console.log(
            `Initializing translations with language: ${initialLanguage}`
        );
        this.setLanguage(initialLanguage);
    }

    /**
     * Determine the initial language to use based on preferences and detection
     */
    private determineInitialLanguage(): string {
        // First priority: saved language preference
        const savedLanguage = this.loadLanguagePreference();
        if (savedLanguage) {
            console.log(`Using saved language preference: ${savedLanguage}`);
            return savedLanguage;
        }

        // Second priority: browser detected language
        const detectedLanguage = this.detectBrowserLanguage();
        if (detectedLanguage && detectedLanguage !== DEFAULT_LANGUAGE_CODE) {
            console.log(`Using browser detected language: ${detectedLanguage}`);
            return detectedLanguage;
        }

        // Final fallback: default language
        console.log(`Using default language: ${DEFAULT_LANGUAGE_CODE}`);
        return DEFAULT_LANGUAGE_CODE;
    }

    /**
     * Translate a key with optional parameters
     */
    translate(key: string, params?: any): Observable<string> {
        // Track key usage for optimization
        this.buildOptimizer.trackKeyUsage(key);

        return this.translateService.get(key, params).pipe(
            catchError((error) => {
                // Handle parameter errors specifically
                if (params && error) {
                    const fallback = this.errorHandler.handleParameterError(
                        key,
                        params,
                        error,
                        this.getCurrentLanguage()
                    );
                    this.translationErrorSubject.next(
                        `Translation parameter error for key: ${key}`
                    );
                    return of(fallback);
                }

                // Handle general translation errors
                const fallback = this.errorHandler.handleMissingTranslation(
                    key,
                    this.getCurrentLanguage()
                );
                this.translationErrorSubject.next(
                    `Translation failed for key: ${key}`
                );
                return of(fallback);
            })
        );
    }

    /**
     * Get instant translation (synchronous)
     */
    instant(key: string, params?: any): string {
        // Track key usage for optimization
        this.buildOptimizer.trackKeyUsage(key);

        try {
            const translation = this.translateService.instant(key, params);

            // Check if translation was found (ngx-translate returns the key if not found)
            if (translation === key) {
                return this.errorHandler.handleMissingTranslation(
                    key,
                    this.getCurrentLanguage()
                );
            }

            return translation;
        } catch (error) {
            // Handle parameter errors specifically
            if (params && error) {
                const fallback = this.errorHandler.handleParameterError(
                    key,
                    params,
                    error,
                    this.getCurrentLanguage()
                );
                this.translationErrorSubject.next(
                    `Translation parameter error for key: ${key}`
                );
                return fallback;
            }

            // Handle general translation errors
            const fallback = this.errorHandler.handleMissingTranslation(
                key,
                this.getCurrentLanguage()
            );
            this.translationErrorSubject.next(
                `Translation failed for key: ${key}`
            );
            return fallback;
        }
    }

    /**
     * Set the current language
     */
    setLanguage(languageCode: string): void {
        const normalizedCode = languageCode.toLowerCase();
        const startTime = performance.now();

        if (!isSupportedLanguage(normalizedCode)) {
            console.warn(
                `Unsupported language: ${languageCode}. Falling back to ${FALLBACK_LANGUAGE_CODE}`
            );
            this.setLanguage(FALLBACK_LANGUAGE_CODE);
            return;
        }

        this.translationLoadingSubject.next(true);

        this.translateService
            .use(normalizedCode)
            .pipe(
                retry({
                    count: 2,
                    delay: (error, retryCount) => {
                        console.log(
                            `Retrying translation load for ${normalizedCode}, attempt ${retryCount}`
                        );
                        return of(null).pipe(delay(1000 * retryCount));
                    },
                }),
                tap(() => {
                    // Record successful load time
                    const loadTime = performance.now() - startTime;
                    this.performanceService.recordLoadTime(
                        normalizedCode,
                        loadTime
                    );

                    // Update cache statistics
                    const cacheStats = this.getCacheStats();
                    this.performanceService.updateCacheHitRatio(
                        cacheStats.hitRatio
                    );
                    this.performanceService.updateMemoryUsage(
                        cacheStats.totalMemoryUsage
                    );
                }),
                catchError((error) => {
                    // Record error
                    this.performanceService.recordError(normalizedCode, error);

                    // Use error handler to process the loading error
                    return this.errorHandler.handleLoadingError(
                        normalizedCode,
                        error
                    );
                })
            )
            .subscribe({
                next: () => {
                    this.saveLanguagePreference(normalizedCode);
                    this.translationLoadingSubject.next(false);
                    console.log(`Language changed to: ${normalizedCode}`);
                },
                error: (translationError: TranslationError) => {
                    const userMessage =
                        this.errorHandler.createUserFriendlyErrorMessage(
                            normalizedCode
                        );
                    this.translationErrorSubject.next(userMessage);
                    this.translationLoadingSubject.next(false);

                    // Record error in performance service
                    this.performanceService.recordError(
                        normalizedCode,
                        translationError
                    );

                    // Implement graceful degradation
                    this.handleLanguageLoadingFailure(
                        normalizedCode,
                        translationError
                    );
                },
            });
    }

    /**
     * Get the current language code
     */
    getCurrentLanguage(): string {
        return (
            this.translateService.getCurrentLang() ||
            this.currentLanguageSubject.value
        );
    }

    /**
     * Get available languages
     */
    getAvailableLanguages(): LanguageConfig[] {
        return SUPPORTED_LANGUAGES;
    }

    /**
     * Get current language configuration
     */
    getCurrentLanguageConfig(): LanguageConfig {
        const currentLang = this.getCurrentLanguage();
        return getLanguageConfig(currentLang) || getDefaultLanguageConfig();
    }

    /**
     * Check if a language is supported
     */
    isLanguageSupported(languageCode: string): boolean {
        return isSupportedLanguage(languageCode.toLowerCase());
    }

    /**
     * Detect browser language with comprehensive fallback logic
     */
    detectBrowserLanguage(): string {
        try {
            // Try multiple browser language detection methods
            const browserLanguages = [
                navigator.language,
                (navigator as any).userLanguage,
                (navigator as any).browserLanguage,
                (navigator as any).systemLanguage,
                ...(navigator.languages || []),
            ].filter(Boolean);

            console.log('Detected browser languages:', browserLanguages);

            // Check each detected language
            for (const browserLang of browserLanguages) {
                if (typeof browserLang === 'string') {
                    // Extract language code (e.g., 'fr-FR' -> 'fr', 'en-US' -> 'en')
                    const langCode = browserLang.split('-')[0].toLowerCase();

                    if (isSupportedLanguage(langCode)) {
                        console.log(
                            `Browser language detected and supported: ${langCode}`
                        );
                        return langCode;
                    }
                }
            }

            // If no supported language found, check for partial matches
            for (const browserLang of browserLanguages) {
                if (typeof browserLang === 'string') {
                    const fullLangCode = browserLang.toLowerCase();

                    // Check if any supported language is contained in the browser language
                    for (const supportedLang of SUPPORTED_LANGUAGES) {
                        if (fullLangCode.includes(supportedLang.code)) {
                            console.log(
                                `Partial browser language match found: ${supportedLang.code}`
                            );
                            return supportedLang.code;
                        }
                    }
                }
            }

            console.log(
                `No supported browser language found. Using default: ${DEFAULT_LANGUAGE_CODE}`
            );
            return DEFAULT_LANGUAGE_CODE;
        } catch (error) {
            console.error('Error detecting browser language:', error);
            return DEFAULT_LANGUAGE_CODE;
        }
    }

    /**
     * Save language preference to storage
     */
    saveLanguagePreference(languageCode: string): void {
        this.storageService.setLanguagePreference(languageCode);
    }

    /**
     * Load language preference from storage
     */
    loadLanguagePreference(): string | null {
        const preference = this.storageService.getLanguagePreference();

        if (
            preference &&
            preference.code &&
            isSupportedLanguage(preference.code)
        ) {
            return preference.code;
        }

        return null;
    }

    /**
     * Clear language preference
     */
    clearLanguagePreference(): void {
        this.storageService.clearLanguagePreference();
    }

    /**
     * Handle missing translation keys (deprecated - use error handler)
     * @deprecated Use TranslationErrorHandlerService.handleMissingTranslation instead
     */
    private handleMissingTranslation(key: string): string {
        return this.errorHandler.handleMissingTranslation(
            key,
            this.getCurrentLanguage()
        );
    }

    /**
     * Handle language loading failure with graceful degradation
     */
    private handleLanguageLoadingFailure(
        languageCode: string,
        error: TranslationError
    ): void {
        console.error(`Failed to load language ${languageCode}:`, error);

        // If this is not already the fallback language, try fallback
        if (languageCode !== FALLBACK_LANGUAGE_CODE) {
            console.log(`Attempting fallback to ${FALLBACK_LANGUAGE_CODE}`);
            this.setLanguage(FALLBACK_LANGUAGE_CODE);
            return;
        }

        // If even fallback failed, try to use cached translations or provide minimal functionality
        if (languageCode === FALLBACK_LANGUAGE_CODE) {
            console.error(
                'Even fallback language failed to load. Using emergency fallback.'
            );
            this.handleEmergencyFallback();
        }
    }

    /**
     * Handle emergency fallback when all translation loading fails
     */
    private handleEmergencyFallback(): void {
        // Set a minimal translation object to prevent complete failure
        const emergencyTranslations = {
            'common.buttons.login': 'Login',
            'common.buttons.signup': 'Sign Up',
            'common.buttons.cancel': 'Cancel',
            'common.buttons.save': 'Save',
            'common.navigation.home': 'Home',
            'common.navigation.settings': 'Settings',
            'common.error.generic': 'An error occurred',
            'common.loading': 'Loading...',
        };

        // Manually set translations for emergency use
        this.translateService.setTranslation(
            FALLBACK_LANGUAGE_CODE,
            emergencyTranslations,
            true
        );
        this.translateService.use(FALLBACK_LANGUAGE_CODE);

        console.warn(
            'Emergency translations loaded. Some features may not work correctly.'
        );
    }

    /**
     * Check if translations are currently loading
     */
    isLoading(): boolean {
        return this.translationLoadingSubject.value;
    }

    /**
     * Get current translation error
     */
    getCurrentError(): string | null {
        return this.translationErrorSubject.value;
    }

    /**
     * Clear current translation error
     */
    clearError(): void {
        this.translationErrorSubject.next(null);
    }

    /**
     * Reload current language translations
     */
    reloadTranslations(): void {
        const currentLang = this.getCurrentLanguage();
        this.translateService.reloadLang(currentLang).subscribe({
            next: () => {
                console.log(
                    `Translations reloaded for language: ${currentLang}`
                );
            },
            error: (error) => {
                console.error(
                    `Failed to reload translations for ${currentLang}:`,
                    error
                );
                this.translationErrorSubject.next(
                    `Failed to reload translations`
                );
            },
        });
    }

    /**
     * Get translation with fallback
     */
    getWithFallback(
        key: string,
        fallback: string,
        params?: any
    ): Observable<string> {
        return this.translate(key, params).pipe(
            map((translation) => {
                // If translation equals the key, it means no translation was found
                return translation === key ? fallback : translation;
            })
        );
    }

    /**
     * Get instant translation with fallback
     */
    instantWithFallback(key: string, fallback: string, params?: any): string {
        const translation = this.instant(key, params);
        const missingTranslation = this.errorHandler.handleMissingTranslation(
            key,
            this.getCurrentLanguage()
        );
        return translation === missingTranslation ? fallback : translation;
    }

    /**
     * Get error handler instance for external use
     */
    getErrorHandler(): TranslationErrorHandlerService {
        return this.errorHandler;
    }

    /**
     * Check if there are critical translation errors
     */
    hasCriticalErrors(): boolean {
        return this.errorHandler.hasCriticalErrors();
    }

    /**
     * Get translation error statistics
     */
    getErrorStats(): { [key: string]: number } {
        return this.errorHandler.getErrorStats();
    }

    /**
     * Attempt to recover from translation errors
     */
    attemptRecovery(): Observable<boolean> {
        const currentLang = this.getCurrentLanguage();

        return new Observable((observer) => {
            // Clear current errors
            this.clearError();
            this.errorHandler.clearErrorHistory();

            // Try to reload current language
            this.translationLoadingSubject.next(true);

            this.translateService
                .reloadLang(currentLang)
                .pipe(
                    retry({
                        count: 3,
                        delay: (error, retryCount) => {
                            console.log(
                                `Recovery attempt ${retryCount} for ${currentLang}`
                            );
                            return of(null).pipe(delay(2000 * retryCount));
                        },
                    })
                )
                .subscribe({
                    next: () => {
                        this.translationLoadingSubject.next(false);
                        console.log(
                            `Translation recovery successful for ${currentLang}`
                        );
                        observer.next(true);
                        observer.complete();
                    },
                    error: (error) => {
                        this.translationLoadingSubject.next(false);
                        console.error(
                            `Translation recovery failed for ${currentLang}:`,
                            error
                        );

                        // Try fallback language as last resort
                        if (currentLang !== FALLBACK_LANGUAGE_CODE) {
                            this.setLanguage(FALLBACK_LANGUAGE_CODE);
                            observer.next(false);
                        } else {
                            observer.next(false);
                        }
                        observer.complete();
                    },
                });
        });
    }

    /**
     * Validate translation file integrity
     */
    validateTranslations(languageCode: string): Observable<boolean> {
        return new Observable((observer) => {
            // Use the stream method to get translations
            this.translateService.stream(languageCode).subscribe({
                next: (translations: any) => {
                    try {
                        // Basic validation - check if it's an object and has some expected keys
                        const isValid =
                            translations &&
                            typeof translations === 'object' &&
                            Object.keys(translations).length > 0;

                        if (isValid) {
                            console.log(
                                `Translation validation passed for ${languageCode}`
                            );
                            observer.next(true);
                        } else {
                            console.warn(
                                `Translation validation failed for ${languageCode}: Invalid structure`
                            );
                            observer.next(false);
                        }
                    } catch (error) {
                        console.error(
                            `Translation validation error for ${languageCode}:`,
                            error
                        );
                        observer.next(false);
                    }
                    observer.complete();
                },
                error: (error: any) => {
                    console.error(
                        `Translation validation failed for ${languageCode}:`,
                        error
                    );
                    observer.next(false);
                    observer.complete();
                },
            });
        });
    }

    /**
     * Preload translations for better performance
     */
    preloadTranslations(languages: string[] = []): Observable<any> {
        const languagesToPreload =
            languages.length > 0
                ? languages
                : SUPPORTED_LANGUAGES.map((lang) => lang.code);

        console.log(
            'Preloading translations for languages:',
            languagesToPreload
        );

        return this.lazyLoader.warmUpCache(languagesToPreload).pipe(
            tap(() => {
                console.log('Translation preloading completed');
            }),
            catchError((error) => {
                console.error('Translation preloading failed:', error);
                return of(null);
            })
        );
    }

    /**
     * Get cache statistics for performance monitoring
     */
    getCacheStats(): {
        size: number;
        maxSize: number;
        languages: string[];
        totalMemoryUsage: number;
        hitRatio: number;
    } {
        const stats = this.lazyLoader.getCacheStats();
        return {
            ...stats,
            hitRatio: this.lazyLoader.getCacheHitRatio(),
        };
    }

    /**
     * Clear translation cache
     */
    clearTranslationCache(language?: string): void {
        this.lazyLoader.clearCache(language);
        if (language) {
            console.log(`Translation cache cleared for ${language}`);
        } else {
            console.log('All translation cache cleared');
        }
    }

    /**
     * Optimize translation cache
     */
    optimizeTranslationCache(): void {
        this.lazyLoader.optimizeCache();
        console.log('Translation cache optimized');
    }

    /**
     * Check if a translation is cached
     */
    isTranslationCached(language: string): boolean {
        return this.lazyLoader.isCached(language);
    }

    /**
     * Check if a translation is currently loading
     */
    isTranslationLoading(language: string): boolean {
        return this.lazyLoader.isLoading(language);
    }

    /**
     * Preload a specific language
     */
    preloadLanguage(language: string): Observable<any> {
        if (!isSupportedLanguage(language)) {
            console.warn(`Cannot preload unsupported language: ${language}`);
            return of(null);
        }

        return this.lazyLoader.preloadTranslation(language).pipe(
            tap(() => {
                console.log(`Language ${language} preloaded successfully`);
            }),
            catchError((error) => {
                console.error(`Failed to preload language ${language}:`, error);
                return of(null);
            })
        );
    }

    /**
     * Batch preload multiple languages
     */
    batchPreloadLanguages(languages: string[]): Observable<any[]> {
        const validLanguages = languages.filter((lang) =>
            isSupportedLanguage(lang)
        );

        if (validLanguages.length === 0) {
            console.warn('No valid languages to preload');
            return of([]);
        }

        const preloadObservables = validLanguages.map((lang) =>
            this.preloadLanguage(lang)
        );

        return forkJoin(preloadObservables).pipe(
            tap(() => {
                console.log(
                    `Batch preloading completed for languages: ${validLanguages.join(
                        ', '
                    )}`
                );
            }),
            catchError((error) => {
                console.error('Batch preloading failed:', error);
                return of([]);
            })
        );
    }

    /**
     * Get memory usage information
     */
    getMemoryUsage(): {
        cacheMemoryUsage: number;
        estimatedTotalUsage: number;
    } {
        const cacheStats = this.getCacheStats();
        return {
            cacheMemoryUsage: cacheStats.totalMemoryUsage,
            estimatedTotalUsage: cacheStats.totalMemoryUsage * 1.2, // Add 20% overhead estimation
        };
    }

    /**
     * Configure lazy loader settings
     */
    configureLazyLoader(config: {
        cacheTimeout?: number;
        maxCacheSize?: number;
        enableCompression?: boolean;
        enableVersioning?: boolean;
    }): void {
        this.lazyLoader.updateConfig(config);
        console.log('Lazy loader configuration updated:', config);
    }

    /**
     * Get performance metrics
     */
    getPerformanceMetrics(): Observable<any> {
        return this.performanceService.metrics$;
    }

    /**
     * Get performance summary
     */
    getPerformanceSummary(): any {
        return this.performanceService.getPerformanceSummary();
    }

    /**
     * Check if performance is degraded
     */
    isPerformanceDegraded(): boolean {
        return this.performanceService.isPerformanceDegraded();
    }

    /**
     * Get performance recommendations
     */
    getPerformanceRecommendations(): string[] {
        return this.performanceService.getPerformanceRecommendations();
    }

    /**
     * Reset performance metrics
     */
    resetPerformanceMetrics(): void {
        this.performanceService.resetMetrics();
    }

    /**
     * Export performance data
     */
    exportPerformanceData(): any {
        return this.performanceService.exportPerformanceData();
    }

    /**
     * Configure performance monitoring
     */
    configurePerformanceMonitoring(config: {
        enableMonitoring?: boolean;
        monitoringInterval?: number;
        maxMetricsHistory?: number;
        enableAutoOptimization?: boolean;
        optimizationThreshold?: number;
    }): void {
        this.performanceService.updateConfig(config);
        console.log('Performance monitoring configuration updated:', config);
    }

    /**
     * Get build optimization report
     */
    getBuildOptimizationReport(): Observable<any> {
        return this.buildOptimizer.generateOptimizationReport();
    }

    /**
     * Get key usage statistics
     */
    getKeyUsageStats(): Map<string, number> {
        return this.buildOptimizer.getKeyUsageStats();
    }

    /**
     * Get used translation keys
     */
    getUsedKeys(): string[] {
        return this.buildOptimizer.getUsedKeys();
    }

    /**
     * Analyze bundle optimization opportunities
     */
    analyzeBundleOptimization(): Observable<any> {
        // Get all translation data
        const translations: { [lang: string]: any } = {};

        return new Observable((observer) => {
            const loadPromises = SUPPORTED_LANGUAGES.map((lang) =>
                this.lazyLoader
                    .getTranslation(lang.code)
                    .toPromise()
                    .then((data) => {
                        translations[lang.code] = data;
                    })
                    .catch((error) => {
                        console.warn(
                            `Failed to load ${lang.code} for analysis:`,
                            error
                        );
                    })
            );

            Promise.all(loadPromises)
                .then(() => {
                    this.buildOptimizer
                        .analyzeBundleOptimization(translations)
                        .subscribe({
                            next: (analysis) => observer.next(analysis),
                            error: (error) => observer.error(error),
                            complete: () => observer.complete(),
                        });
                })
                .catch((error) => {
                    observer.error(error);
                });
        });
    }

    /**
     * Optimize translation object for production
     */
    optimizeTranslationForProduction(
        translations: any,
        usedKeys?: string[]
    ): Observable<any> {
        const keysToUse = usedKeys || this.getUsedKeys();
        return this.buildOptimizer.optimizeTranslationObject(
            translations,
            keysToUse
        );
    }

    /**
     * Configure build optimizer
     */
    configureBuildOptimizer(config: {
        enableTreeShaking?: boolean;
        enableCompression?: boolean;
        enableMinification?: boolean;
        removeUnusedKeys?: boolean;
        optimizeForProduction?: boolean;
        bundleAnalysis?: boolean;
    }): void {
        this.buildOptimizer.updateConfig(config);
        console.log('Build optimizer configuration updated:', config);
    }

    /**
     * Reset build optimization tracking
     */
    resetBuildOptimizationTracking(): void {
        this.buildOptimizer.resetTracking();
        console.log('Build optimization tracking reset');
    }

    /**
     * Export build optimization data
     */
    exportBuildOptimizationData(): any {
        return this.buildOptimizer.exportTrackingData();
    }

    /**
     * Get comprehensive optimization recommendations
     */
    getOptimizationRecommendations(): Observable<{
        performance: string[];
        build: any;
        combined: string[];
    }> {
        return new Observable((observer) => {
            const performanceRecommendations =
                this.getPerformanceRecommendations();

            this.getBuildOptimizationReport().subscribe({
                next: (buildReport) => {
                    const combined = [
                        ...performanceRecommendations,
                        ...buildReport.recommendations,
                    ];

                    observer.next({
                        performance: performanceRecommendations,
                        build: buildReport,
                        combined: Array.from(new Set(combined)), // Remove duplicates
                    });
                    observer.complete();
                },
                error: (error) => {
                    console.error(
                        'Failed to get build optimization report:',
                        error
                    );
                    observer.next({
                        performance: performanceRecommendations,
                        build: { recommendations: [] },
                        combined: performanceRecommendations,
                    });
                    observer.complete();
                },
            });
        });
    }
}
